import EventEmitter from 'eventemitter3';
import { FuseRenderer } from '@/renderer';

/**
 * `immersive-ar` is passthrough — the room is the backdrop and the app draws
 * over it. `immersive-vr` replaces the view entirely.
 *
 * The distinction is not only a look: passthrough AR is where anchors, planes
 * and hit-testing live, and it is the mode with the narrowest device support —
 * Safari on visionOS, for instance, offers `immersive-vr` and not
 * `immersive-ar`. A VR session must therefore never be handed the room-scanning
 * feature set, which is why the defaults below are chosen per mode.
 */
export type FuseXRMode = 'immersive-ar' | 'immersive-vr';

/** Optional features worth asking for per mode; missing ones degrade quietly. */
const OPTIONAL_FEATURES: Record<FuseXRMode, string[]> = {
  'immersive-ar': ['hit-test', 'anchors', 'hand-tracking', 'plane-detection'],
  // Nothing about a room applies when the room isn't there. Asking anyway is
  // not harmless: on a runtime that treats an unknown optional feature as a
  // hard error, it costs the whole session.
  'immersive-vr': ['hand-tracking'],
};

export type FuseXRSessionOptions = {
  renderer: FuseRenderer;
  /** Session type. Default `immersive-ar` — the passthrough range. */
  mode?: FuseXRMode;
  /** XR framebuffer scale. Must be set before the session starts. */
  framebufferScale?: number;
  /** Fixed foveation 0 (none) to 1 (max). Applied live after session start. */
  foveation?: number;
  /**
   * Preferred display refresh rate, Hz. The closest rate the device supports
   * is chosen. Default: the highest offered. A rate the app can HOLD beats a
   * higher one it misses — passthrough shares the compositor, so app frame
   * drops read as passthrough lag.
   */
  targetFrameRate?: number;
  requiredFeatures?: string[];
  optionalFeatures?: string[];
};

interface EventMap {
  sessionstart: () => void;
  sessionend: () => void;
  /** The reference space was reset (e.g. Meta-button recenter). */
  referencereset: () => void;
}

/**
 * Lifecycle wrapper for an immersive-ar (passthrough) WebXR session.
 * Keeps session management out of FuseRenderer and reusable across apps.
 */
export class FuseXRSession extends EventEmitter<EventMap> {
  session?: XRSession;
  referenceSpace?: XRReferenceSpace;

  #renderer: FuseRenderer;
  #options: FuseXRSessionOptions;

  constructor(options: FuseXRSessionOptions) {
    super();
    this.#renderer = options.renderer;
    this.#options = options;
  }

  static async isSupported(mode: FuseXRMode = 'immersive-ar'): Promise<boolean> {
    if (!navigator.xr) return false;
    try {
      return await navigator.xr.isSessionSupported(mode);
    } catch {
      return false;
    }
  }

  /** The mode this session was configured for. */
  get mode(): FuseXRMode {
    return this.#options.mode ?? 'immersive-ar';
  }

  get isPresenting(): boolean {
    return this.#xr?.isPresenting ?? false;
  }

  get #xr() {
    return this.#renderer.xr;
  }

  /** Whether the granted session includes a feature (hit-test, anchors, …). */
  hasFeature(name: string): boolean {
    return !!(this.session as any)?.enabledFeatures?.includes(name);
  }

  /** Must be called from a user-gesture handler (e.g. an Enter MR button). */
  async start(): Promise<XRSession> {
    if (!navigator.xr) {
      throw new Error('WebXR is not available in this browser');
    }
    if (this.session) return this.session;

    const xr = this.#xr;
    xr.setFramebufferScaleFactor(this.#options.framebufferScale ?? 1.0);
    xr.setReferenceSpaceType('local-floor');

    // Only local-floor is truly required — hit-test/anchors have graceful
    // fallbacks downstream, and requiring them would reject the session
    // outright on runtimes that lack them.
    const session = await navigator.xr.requestSession(this.mode, {
      requiredFeatures: this.#options.requiredFeatures ?? ['local-floor'],
      optionalFeatures: this.#options.optionalFeatures ?? OPTIONAL_FEATURES[this.mode],
    });

    try {
      await xr.setSession(session);
    } catch (error) {
      // Never keep a session that three failed to attach — a poisoned
      // this.session would short-circuit every future start()
      try { await session.end(); } catch { /* already dead */ }
      throw error;
    }
    this.session = session;

    // Registered AFTER setSession so three's own teardown listener (added
    // inside setSession) runs first and xr state is clean when we emit
    session.addEventListener('end', () => {
      this.session = undefined;
      this.referenceSpace = undefined;
      this.#renderer.setXRExposure(false);
      this.emit('sessionend');
    });

    xr.setFoveation(this.#options.foveation ?? 0.4);

    // Prefer the highest refresh rate the device offers (Quest defaults 72 Hz),
    // or the closest supported rate to an explicit target
    try {
      const rates = (session as any).supportedFrameRates as Float32Array | undefined;
      if (rates?.length && (session as any).updateTargetFrameRate) {
        const wanted = this.#options.targetFrameRate;
        const rate = wanted === undefined
          ? Math.max(...rates)
          : [...rates].reduce((best, r) => Math.abs(r - wanted) < Math.abs(best - wanted) ? r : best);
        await (session as any).updateTargetFrameRate(rate);
        console.log(`XR refresh rate: ${rate} Hz (supported: ${[...rates].join(', ')})`);
      }
    } catch (error) {
      console.warn('Could not raise target frame rate', error);
    }

    this.referenceSpace = xr.getReferenceSpace() ?? undefined;
    // Meta-button recenter fires 'reset' — re-read the space and let the app
    // decide whether the world needs re-placing
    this.referenceSpace?.addEventListener?.('reset', () => {
      this.referenceSpace = xr.getReferenceSpace() ?? this.referenceSpace;
      this.emit('referencereset');
    });

    this.#renderer.setXRExposure(true);
    this.emit('sessionstart');
    return session;
  }

  async end(): Promise<void> {
    if (this.session) {
      await this.session.end();
    }
  }
}
